import { CSRF_URL, oauthConfig, REVOKE_URL } from "../config/oauth.config";

import {
  ACTIVE_TENANT_COOKIE,
  BASE_DOMAIN,
  LOGOUT_INTENT_KEY,
  LOGOUT_INTENT_PARAM,
  LOGOUT_INTENT_TTL_MS,
  LOGOUT_ROUTE,
  OAUTH_STATE_KEY,
} from "./constants";
import type { OAuthState } from "./types";

// Allows only same-origin relative paths.
// Rejects: external URLs, protocol-relative //, data:, javascript:, or malformed strings.
const SAFE_REDIRECT_RE = /^\/[a-zA-Z0-9/_\-?=&#]*$/;

export function validateRedirect(raw: unknown): string | null {
  if (typeof raw !== "string" || !raw) return null;
  if (raw.startsWith("http") || raw.startsWith("//")) return null;
  if (!SAFE_REDIRECT_RE.test(raw)) return null;
  return raw;
}

export function storeAuthState(state: Pick<OAuthState, "redirect" | "tenant">): void {
  const full: OAuthState = {
    ...state,
    nonce: crypto.randomUUID(),
    v: 1,
  };
  sessionStorage.setItem(OAUTH_STATE_KEY, JSON.stringify(full));
}

// Reads and removes the stored OAuth state. Returns null on parse failure or version mismatch.
export function consumeAuthState(): OAuthState | null {
  const raw = sessionStorage.getItem(OAUTH_STATE_KEY);
  sessionStorage.removeItem(OAUTH_STATE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as OAuthState;
    if (parsed.v !== 1) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function extractTenant(hostname: string, baseDomain: string): string | null {
  if (!hostname.endsWith(baseDomain)) return null;
  const withoutBase = hostname.slice(0, hostname.length - baseDomain.length);
  const sub = withoutBase.replace(/\.$/, "");
  return sub || null;
}

export function getCurrentTenant(): string | null {
  if (typeof window === "undefined") return null;
  return extractTenant(window.location.hostname, BASE_DOMAIN);
}

// Builds an absolute URL back to the tenant subdomain for cross-origin post-auth navigation.
export function buildTenantUrl(tenant: string, path: string): string {
  const { protocol, port } = window.location;
  const portSuffix = port ? `:${port}` : "";
  return `${protocol}//${tenant}.${BASE_DOMAIN}${portSuffix}${path}`;
}

// Maps a raw OAuth/library error string to a normalized message suitable for the UI.
export function normalizeAuthError(raw: unknown): string {
  if (typeof raw !== "string" || !raw) return "An unexpected authentication error occurred.";
  const lower = raw.toLowerCase();
  if (lower.includes("access_denied")) return "Access was denied. Please try again.";
  if (lower.includes("invalid_client")) return "OAuth client configuration error.";
  if (lower.includes("invalid_grant")) return "Authorization code expired or already used.";

  return raw;
}

// Cookie helpers for single-client session enforcement (LOA-XXX).
// The cookie is set on the root domain so it's shared across all tenant subdomains.

export function setActiveTenantCookie(tenant: string): void {
  const domain = `.${BASE_DOMAIN}`;
  // Only add Secure flag on HTTPS - localhost typically uses HTTP
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${ACTIVE_TENANT_COOKIE}=${tenant}; domain=${domain}; path=/; SameSite=Lax${secure}`;
}

export function getActiveTenantCookie(): string | null {
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${ACTIVE_TENANT_COOKIE}=([^;]*)`)
  );

  return match ? decodeURIComponent(match[1]) : null;
}

export function clearActiveTenantCookie(): void {
  const domain = `.${BASE_DOMAIN}`;
  document.cookie = `${ACTIVE_TENANT_COOKIE}=; domain=${domain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

interface StoredLogoutIntent {
  nonce: string;
  markedAt: number;
}

/**
 * LOA-1617: record that the user asked to be signed out, and return the nonce
 * that unlocks it.
 *
 * Call this on the click that starts a logout, then put the returned nonce in
 * the logout route's URL (see LOGOUT_INTENT_PARAM). The marker itself lives in
 * sessionStorage, which survives the full-page navigation to that route but is
 * unreadable from any other origin; the nonce is what ties the marker to the
 * specific navigation it was recorded for.
 *
 * Returns an empty string if storage is unavailable, in which case the route
 * falls back to asking the user.
 */
export function markLogoutIntent(): string {
  const nonce = crypto.randomUUID();
  const intent: StoredLogoutIntent = { nonce, markedAt: Date.now() };

  try {
    sessionStorage.setItem(LOGOUT_INTENT_KEY, JSON.stringify(intent));
  } catch (error) {
    // Private mode or blocked storage. The logout route will ask the user to
    // confirm instead of signing out on its own — annoying, not broken.
    console.warn("[markLogoutIntent] could not record logout intent", error);

    return "";
  }

  return nonce;
}

/** The logout route URL carrying ``nonce`` from markLogoutIntent. */
export function logoutRouteWithIntent(nonce: string): string {
  if (!nonce) {
    return LOGOUT_ROUTE;
  }

  return `${LOGOUT_ROUTE}?${LOGOUT_INTENT_PARAM}=${encodeURIComponent(nonce)}`;
}

/** The logout-intent nonce on the current URL, if it carries one. */
export function readLogoutIntentNonce(): string | null {
  return new URLSearchParams(window.location.search).get(LOGOUT_INTENT_PARAM);
}

/**
 * LOA-1617: read and clear a recorded logout intent, checking it against the
 * nonce the current navigation carries.
 *
 * The backend endpoint is unforgeable now, but the logout ROUTE is still a
 * page any site can navigate a victim to, and it would otherwise fetch a
 * token and submit the form on its own — reinstating the forced logout
 * pentest F-005 describes, one step removed.
 *
 * Two things must line up, which is why the stored marker alone is not
 * enough: a cross-site navigation cannot write this origin's sessionStorage,
 * and it cannot guess the nonce of a marker left behind by a logout the user
 * started but did not finish (cancelling HQ's logout modal, say). Either one
 * on its own leaves a window where a drive-by navigation would be taken for
 * the real thing.
 *
 * One-shot: the marker is removed as it is read. Fails closed — a missing,
 * malformed, expired or unreadable marker, an absent nonce, or any mismatch
 * all count as no intent.
 */
export function consumeLogoutIntent(nonce: string | null): boolean {
  let raw: string | null = null;

  try {
    raw = sessionStorage.getItem(LOGOUT_INTENT_KEY);
    sessionStorage.removeItem(LOGOUT_INTENT_KEY);
  } catch (error) {
    console.warn("[consumeLogoutIntent] could not read logout intent", error);

    return false;
  }

  if (raw === null || !nonce) {
    return false;
  }

  let intent: StoredLogoutIntent;

  try {
    intent = JSON.parse(raw) as StoredLogoutIntent;
  } catch (error) {
    console.warn("[consumeLogoutIntent] discarding malformed intent", error);

    return false;
  }

  if (typeof intent?.nonce !== "string" || !Number.isFinite(intent.markedAt)) {
    return false;
  }

  if (intent.nonce !== nonce) {
    return false;
  }

  return Date.now() - intent.markedAt < LOGOUT_INTENT_TTL_MS;
}

/**
 * LOA-1617: fetch a CSRF token for the logout POST.
 *
 * The backend made logout a CSRF-protected POST (pentest F-005: as a GET,
 * any site could force a logout with a top-level navigation). The paired
 * CSRF cookie belongs to the API origin, which this origin cannot read, so
 * the API hands the token out over a credentialed request instead — the same
 * response also sets the cookie the token is checked against.
 *
 * Returns null on any failure. The caller decides what to do with that; a
 * token is not a secret, so there is nothing to protect by being silent.
 */
export async function fetchCsrfToken(): Promise<string | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 5000);

  try {
    const response = await fetch(CSRF_URL, {
      method: "GET",
      credentials: "include",
      signal: controller.signal,
    });

    if (!response.ok) {
      console.warn(`[fetchCsrfToken] returned ${response.status}`);

      return null;
    }

    const data = (await response.json()) as { csrfToken?: string };

    return data.csrfToken ?? null;
  } catch (error) {
    console.warn("[fetchCsrfToken] request failed", error);

    return null;
  } finally {
    clearTimeout(timer);
  }
}

type RevocableTokenType = "access_token" | "refresh_token";

function readRocpValue(key: string): string | null {
  const raw = localStorage.getItem(key);

  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as string;

    return parsed || null;
  } catch {
    return null;
  }
}

/**
 * LOA-1613: the access token currently stored for this origin (shared across
 * tabs via localStorage). Used by the 401 handler to tell a dead request for
 * THIS session apart from a stale one for a session another tab has already
 * replaced with a fresh login.
 */
export function getStoredAccessToken(): string | null {
  return readRocpValue("ROCP_token");
}

async function revokeOne(token: string, tokenTypeHint: RevocableTokenType): Promise<void> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 2000);

  try {
    const response = await fetch(REVOKE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        token,
        token_type_hint: tokenTypeHint,
        client_id: oauthConfig.clientId,
      }),
      signal: controller.signal,
      // Both call sites fire this immediately before navigating away
      // (window.location.replace/href), which would otherwise cancel this
      // request mid-flight as the old document unloads.
      keepalive: true,
    });

    // fetch only rejects on a transport failure — an HTTP error status
    // still resolves normally. RFC 7009 returns 200 even for an unknown or
    // already-revoked token (anti-scanning), so a non-ok response here is a
    // genuine failure (bad client_id, 5xx, …), not that expected no-op.
    if (!response.ok) {
      console.warn(
        `[revokeTokens] ${tokenTypeHint} revocation returned ${response.status}; ` +
          "the token may still be usable until it naturally expires.",
      );
    }
  } catch (error) {
    // Network error, timeout/abort, or CORS failure. Logout must never get
    // stuck on this — clearing local state and redirecting is all that
    // matters from here — but it must not be silent, since the token being
    // revoked is about to be erased from local storage either way.
    console.warn(
      `[revokeTokens] ${tokenTypeHint} revocation failed; ` +
        "the token may still be usable until it naturally expires.",
      error,
    );
  } finally {
    clearTimeout(timer);
  }
}

/**
 * LOA-1613: best-effort RFC 7009 revocation of THIS session's own access and
 * refresh tokens, via django-oauth-toolkit's standard `/o/revoke_token/`
 * endpoint. Revokes only these two tokens, not every token the user holds —
 * unlike the backend's same-tenant logout view, which (by design, see
 * apps/authentication/views.py) only flushes the Django session and leaves
 * OAuth tokens live, precisely so a plain logout doesn't sign the user out
 * of other devices/tabs.
 *
 * MUST be called before clearClientAuthState() wipes ROCP_* from
 * localStorage, or there is nothing left here to revoke.
 *
 * Never throws and never blocks logout: a slow/failed network request is
 * bounded by a short per-call timeout, so logout always proceeds to clear
 * local state and redirect regardless of whether revocation actually
 * succeeded server-side. A failure (HTTP error or transport failure) is
 * logged via console.warn rather than silently discarded, since the token
 * it was trying to revoke is about to become unobservable once local
 * storage is cleared.
 */
export async function revokeTokens(): Promise<void> {
  const accessToken = readRocpValue("ROCP_token");
  const refreshToken = readRocpValue("ROCP_refreshToken");

  await Promise.allSettled(
    [
      accessToken ? revokeOne(accessToken, "access_token") : null,
      refreshToken ? revokeOne(refreshToken, "refresh_token") : null,
    ].filter((call): call is Promise<void> => call !== null),
  );
}

/**
 * Purge every client-side OAuth credential for this origin: the
 * react-oauth2-code-pkce tokens in localStorage (shared across tabs) and the
 * pending OAuth flow state in sessionStorage.
 *
 * Must run on every path that invalidates the session — including the 401
 * handler, since a server-side revocation (cross-tenant logout) leaves
 * `ROCP_token` behind, and `hasStoredOAuthToken()` would then hold the
 * protected layout on its initializing spinner instead of letting RouteGuard
 * redirect to login.
 *
 * CRITICAL: Do NOT call the library's logOut() instead — it writes NEW stale
 * PKCE state that breaks a fresh login in another tab.
 */
export function clearClientAuthState(): void {
  const ropKeys = Object.keys(localStorage).filter((k) => k.startsWith("ROCP_"));

  ropKeys.forEach((k) => localStorage.removeItem(k));
  sessionStorage.removeItem(OAUTH_STATE_KEY);
}

/**
 * Check if a token exists in localStorage from the react-oauth2-code-pkce library.
 *
 * This is a workaround for timing issues where the library hasn't yet read the
 * token from localStorage but the user is actually authenticated. Used to
 * prevent premature redirects to login when opening a new tab with a direct URL.
 *
 * The library uses "ROCP_" prefix for its storage keys and stores values as JSON.
 */
export function hasStoredOAuthToken(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  // react-oauth2-code-pkce stores tokens with ROCP_ prefix as JSON strings
  // Using localStorage (shared across tabs) per oauth.config.ts storage setting
  const token = localStorage.getItem("ROCP_token");
  const idToken = localStorage.getItem("ROCP_idToken");

  // Values are JSON-stringified, so '""' (empty string) or 'null' are invalid
  // A valid token looks like '"abc123..."' (quoted string with content)
  const hasToken = token !== null && token !== '""' && token !== "null" && token.length > 2;
  const hasIdToken = idToken !== null && idToken !== '""' && idToken !== "null";

  return hasToken || hasIdToken;
}
